import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Coffee, Search } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { attendanceService, type UnavailableUser } from '@/services/attendance.service';

// ── Avatar helpers ────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  'bg-violet-100 text-violet-700',
  'bg-emerald-100 text-emerald-700',
  'bg-violet-100 text-violet-700',
  'bg-rose-100 text-rose-700',
  'bg-amber-100 text-amber-700',
  'bg-teal-100 text-teal-700',
  'bg-orange-100 text-orange-700',
  'bg-teal-100 text-teal-700',
];

function avatarColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash * 31) + userId.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]!;
}

function initials(first: string, last: string): string {
  return `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase();
}

// ── Display helpers ───────────────────────────────────────────────────────────

/** "09:00:00" → "9:00 AM" */
function formatClock(time: string): string {
  const [h = 0, m = 0] = time.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${suffix}`;
}

/** 150 → "2h 30m" */
function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function leaveSubtitle(user: UnavailableUser): string {
  if (user.leave_kind === 'short' && user.from_time && user.to_time) {
    const range = `${formatClock(user.from_time)} – ${formatClock(user.to_time)}`;
    const duration = user.duration_minutes ? ` (${formatDuration(user.duration_minutes)})` : '';
    const extra = (user.short_windows?.length ?? 1) - 1;
    return `${range}${duration}${extra > 0 ? ` +${extra} more` : ''}`;
  }
  const kind = !user.is_half_day
    ? 'Full Day'
    : user.half_day_period === 'morning' ? 'Half Day (1st)' : 'Half Day (2nd)';
  return user.leave_type_name ? `${kind} · ${user.leave_type_name}` : kind;
}

function timeAgo(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

// ── Tabs ─────────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'leaves' as const,             label: 'Leaves' },
  { key: 'not_clocked_in' as const,     label: 'Not Clocking In' },
  { key: 'clocked_out_not_in' as const, label: 'Clocked Out – Not In' },
];

type TabKey = (typeof TABS)[number]['key'];

/**
 * Twelve cards is one tidy block at four columns, but a ~800px scroll when the grid
 * collapses to a single column — the pager would sit off-screen below it. Half the page
 * on phones keeps the whole widget within a thumb's reach.
 */
const PAGE_SIZE_MOBILE = 6;
const PAGE_SIZE_DESKTOP = 12;
const SM_BREAKPOINT = '(min-width: 640px)'; // Tailwind's sm:
const REFRESH_INTERVAL_MS = 60_000;

function useIsWideScreen(): boolean {
  const [wide, setWide] = useState(() => window.matchMedia(SM_BREAKPOINT).matches);
  useEffect(() => {
    const mq = window.matchMedia(SM_BREAKPOINT);
    const onChange = (e: MediaQueryListEvent) => setWide(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return wide;
}

// ── Sub-components ────────────────────────────────────────────────────────────

/** Shared by the skeleton and the real list so they can never fall out of step. */
const GRID_CLS = 'grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';

function SkeletonGrid() {
  return (
    <div className={GRID_CLS}>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="h-16 animate-pulse rounded-lg bg-muted/50 sm:h-14" />
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-8">
      <div className="rounded-full bg-teal-50 p-3 dark:bg-teal-900/30">
        <Coffee className="h-5 w-5 text-teal-500" />
      </div>
      <p className="text-sm font-medium">No entries found</p>
      <p className="text-xs text-muted-foreground">Everyone's all set in this category</p>
    </div>
  );
}

function UserCard({ user, tab }: { user: UnavailableUser; tab: TabKey }) {
  const color = avatarColor(user.user_id);
  const inits = initials(user.first_name, user.last_name);

  const subtitle =
    tab === 'leaves'
      ? leaveSubtitle(user)
      : tab === 'not_clocked_in'
        ? 'Not Clocked In'
        : user.last_punch_out
          ? `Clocked out ${timeAgo(user.last_punch_out)}`
          : 'Clocked Out';

  const badge =
    tab === 'leaves' ? (
      user.leave_kind === 'short' ? (
        <span className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-700 sm:px-1.5 sm:text-[10px]">
          Short
        </span>
      ) : user.is_half_day ? (
        <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700 sm:px-1.5 sm:text-[10px]">
          Half
        </span>
      ) : (
        <span className="shrink-0 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700 sm:px-1.5 sm:text-[10px]">
          On Leave
        </span>
      )
    ) : tab === 'not_clocked_in' ? (
      <span className="shrink-0 rounded-full border border-teal-500 px-2 py-0.5 text-[11px] font-semibold text-teal-600 sm:px-1.5 sm:text-[10px]">
        No Punch
      </span>
    ) : (
      <span className="shrink-0 rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-semibold text-orange-700 sm:px-1.5 sm:text-[10px]">
        Out
      </span>
    );

  return (
    // Roomier on phones (one card per row leaves the space, so use it) and back to the
    // dense multi-column treatment from sm: up.
    <div className="flex items-center gap-3 rounded-lg border border-border/50 bg-card/60 p-3 sm:items-start sm:gap-2 sm:p-2">
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full text-sm font-bold sm:h-8 sm:w-8 sm:text-xs ${color}`}
      >
        {user.photo_url ? (
          <img
            src={user.photo_url}
            alt={`${user.first_name} ${user.last_name}`}
            className="h-full w-full object-cover"
          />
        ) : (
          inits
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2 sm:gap-1">
          <p className="truncate text-sm font-semibold uppercase leading-tight sm:text-[11px]">
            {user.first_name} {user.last_name}
          </p>
          {badge}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground sm:text-[10px]">{subtitle}</p>
      </div>
    </div>
  );
}

// ── Main widget ───────────────────────────────────────────────────────────────

export function UnavailableWidget() {
  const [activeTab, setActiveTab] = useState<TabKey>('leaves');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const isWide = useIsWideScreen();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboard', 'unavailable-today'],
    queryFn: () => attendanceService.getUnavailableToday(),
    // A leave approved after the dashboard was opened shows up within a minute.
    refetchInterval: REFRESH_INTERVAL_MS,
  });

  const tabIndex = TABS.findIndex((t) => t.key === activeTab);

  function goTab(key: TabKey) {
    setActiveTab(key);
    setSearch('');
    setPage(1);
  }

  function prevTab() {
    goTab(TABS[(tabIndex - 1 + TABS.length) % TABS.length]!.key);
  }

  function nextTab() {
    goTab(TABS[(tabIndex + 1) % TABS.length]!.key);
  }

  const counts = {
    leaves:             data?.leaves.length ?? 0,
    not_clocked_in:     data?.not_clocked_in.length ?? 0,
    clocked_out_not_in: data?.clocked_out_not_in.length ?? 0,
  };

  const allItems: UnavailableUser[] = useMemo(() => {
    if (data == null) return [];
    const raw =
      activeTab === 'leaves'
        ? data.leaves
        : activeTab === 'not_clocked_in'
          ? data.not_clocked_in
          : data.clocked_out_not_in;
    const seen = new Set<string>();
    return raw.filter((u) => {
      if (seen.has(u.user_id)) return false;
      seen.add(u.user_id);
      return true;
    });
  }, [data, activeTab]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allItems;
    return allItems.filter((u) =>
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(q),
    );
  }, [allItems, search]);

  const pageSize = isWide ? PAGE_SIZE_DESKTOP : PAGE_SIZE_MOBILE;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  // Clamp on render rather than syncing state in an effect: rotating the phone shrinks
  // the page count, and page 2 of 1 would otherwise render an empty grid.
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        {/* ── Header ──
            The arrows step through tabs. They are 44px on touch screens per the design
            system's minimum, and shrink back to a tight icon button from sm: up where a
            mouse makes the extra area unnecessary. */}
        <div className="mb-3 flex items-center gap-1 sm:gap-2">
          <button
            onClick={prevTab}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground sm:h-7 sm:w-7"
            aria-label="Previous tab"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span className="truncate text-sm font-semibold">Today's Unavailable</span>
            {data != null && (
              <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-semibold">
                {data.total}
              </span>
            )}
          </div>

          <button
            onClick={nextTab}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground sm:h-7 sm:w-7"
            aria-label="Next tab"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* ── Tabs ──
            One scrollable row rather than flex-wrap: three labels this long wrapped onto
            two ragged lines at 375px. -mx-1/px-1 lets the active pill's ring reach the
            card edge without being clipped by the scroll container. */}
        <div className="-mx-1 mb-3 flex gap-1.5 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0">
          {TABS.map((t) => {
            const isActive = t.key === activeTab;
            const count = counts[t.key];
            return (
              <button
                key={t.key}
                onClick={() => goTab(t.key)}
                className={`flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-3 py-2 text-xs font-medium transition-colors sm:py-1 ${
                  isActive
                    ? 'border border-teal-600 text-teal-700 dark:text-teal-400'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t.label}
                {count > 0 && (
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                      isActive
                        ? 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ── Search ── */}
        <div className="relative mb-3">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            // text-base on mobile is deliberate: iOS Safari zooms the whole page in when
            // a focused input is under 16px, which strands the user mid-widget.
            className="w-full rounded-md border border-input bg-background py-2.5 pl-8 pr-3 text-base outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-teal-500 sm:py-1.5 sm:text-sm"
          />
        </div>

        {/* ── Content ── */}
        {isLoading ? (
          <SkeletonGrid />
        ) : isError && data == null ? (
          <div className="flex flex-col items-center justify-center gap-2 py-8">
            <p className="text-sm font-medium">Couldn't load today's list</p>
            <button
              onClick={() => void refetch()}
              className="min-h-[44px] rounded-md px-3 text-xs font-medium text-teal-700 transition-colors hover:bg-accent dark:text-teal-400 sm:min-h-0 sm:py-1.5"
            >
              Retry
            </button>
          </div>
        ) : paged.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className={GRID_CLS}>
              {paged.map((u) => (
                <UserCard key={u.user_id} user={u} tab={activeTab} />
              ))}
            </div>

            {totalPages > 1 && (
              // Centred and full-width on mobile so the controls are reachable with a
              // thumb; right-aligned on desktop where they sit under the grid.
              <div className="mt-3 flex items-center justify-center gap-2 sm:justify-end">
                <button
                  disabled={safePage === 1}
                  onClick={() => setPage(safePage - 1)}
                  className="flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent disabled:opacity-40 sm:h-7 sm:w-7"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="min-w-[3.5rem] text-center text-xs tabular-nums text-muted-foreground">
                  {safePage} / {totalPages}
                </span>
                <button
                  disabled={safePage === totalPages}
                  onClick={() => setPage(safePage + 1)}
                  className="flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent disabled:opacity-40 sm:h-7 sm:w-7"
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
